import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  login as apiLogin,
  fetchMe,
  clearTokens,
  getAccessToken,
  setTokens,
} from '../services/authApi';

/**
 * Login with email + password
 */
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      // POST /api/token/ — stores tokens in localStorage
      await apiLogin(email, password);
      // Fetch user profile
      const user = await fetchMe();
      return user;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Validate existing token on app mount
 */
export const validateToken = createAsyncThunk(
  'auth/validateToken',
  async (_, { rejectWithValue }) => {
    try {
      // Check for SSO token in URL query params
      const params = new URLSearchParams(window.location.search);
      const ssoToken = params.get('token');
      const ssoRefresh = params.get('refresh');
      if (ssoToken) {
        setTokens(ssoToken, ssoRefresh || '');
        // Clean tokens from URL without reload
        params.delete('token');
        params.delete('refresh');
        const clean = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (clean ? `?${clean}` : ''));
      }

      const token = getAccessToken();
      if (!token) return rejectWithValue('No token');
      const user = await fetchMe();
      return user;
    } catch (error) {
      clearTokens();
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  user: null,           // { id, email, first_name, last_name, full_name, organization, ... }
  isAuthenticated: false,
  isLoading: true,      // true until initial validateToken resolves
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      clearTokens();
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // loginUser
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Login failed';
      })
      // validateToken
      .addCase(validateToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(validateToken.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
      });
  },
});

export const { logout } = authSlice.actions;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;

/** Returns effective role: 'superadmin' if is_staff, else user.role */
export const selectUserRole = (state) => {
  const user = state.auth.user;
  if (!user) return null;
  if (user.is_staff) return 'superadmin';
  return user.role;
};

export const selectIsEvaluator = (state) => {
  const user = state.auth.user;
  return user?.role === 'evaluator';
};

export const selectIsSuperAdmin = (state) => {
  const user = state.auth.user;
  return !!user?.is_staff;
};

export default authSlice.reducer;
